import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonBackButton,
  IonFooter,
  IonButton,
  IonItem,
  IonLabel,
  IonTextarea,
  IonIcon,
  IonModal,
  IonCard,
  IonToast,
  IonAlert,
  IonChip,
} from "@ionic/react";
import CreateTicket from "../components/CreateTicket";
import CreateTicketPaymentRequest from "../components/CreateTicketPaymentRequest";
import RecordPartsUsed from "../components/RecordPartsUsed";
import { useLocation } from "react-router";
import NewTicket from "./NewTicket";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRecoilValue } from "recoil";
import { userProfileState } from "../services/state.service";
import { useEffect, useState } from "react";
import {
  getTicketById,
  createTicket,
  updateTicket,
  addComment,
  getTicketPaymentRequests,
  cancelTicketPaymentRequest,
  getTicketInventoryUsage,
  reverseTicketInventoryUsage,
} from "../services/ticket.service";
import { images, send } from "ionicons/icons";
import { takePicture, dataUrlToFile } from "../hooks/common";
import { uploadFile } from "../services/image.service";
import useNavigation from "../hooks/useNavigation";

const Ticket: React.FC = () => {
  const userProfile: any = useRecoilValue(userProfileState);
  const location = useLocation();
  const newTicket: any = (location.state as any)?.newTicket ?? false;
  const id: any = (location.state as any)?.ticketId ?? null;
  const isUpdate = (location.state as any)?.updateTicket ?? false;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [chat, setChat] = useState<any[]>([]);
  const [imagePreview, setIsImagePreview] = useState<boolean>(false);
  const [onSuccess, setOnSuccess] = useState("");
  const [onError, setOnError] = useState("");
  const [ticketId, setTicketId] = useState<any>();
  const navigateTo = useNavigation();
  const [loadingState, setLoadingState] = useState(false);
  const [ticketDetails, setTicketDetails] = useState<any>(null);
  const [uploadedAttachment, setUploadedAttachment] = useState<{ url: string; name: string; mime: string } | null>(null);
  const [paymentRequests, setPaymentRequests] = useState<any[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [cancelPaymentId, setCancelPaymentId] = useState<string | null>(null);

  const [partsUsage, setPartsUsage] = useState<any[]>([]);
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [reversePartsId, setReversePartsId] = useState<string | null>(null);

  const isAdmin = !!userProfile?.roles?.some((role: any) => role?.name === 'admin');

  const formik = useFormik({
    initialValues: {
      customerUserId: "",
      customerMobile: "",
      farmId: "",
      plotId: "",
      category: "",
      priority: "normal",
      assigneeId: "",
      assigneeName: "",
      description: "",
      message: "",
      img: "",
    },
    validationSchema: Yup.object({
      customerUserId: Yup.string().trim().required("Required"),
      customerMobile: Yup.string().trim().required("Required"),
      category: Yup.string().trim().required("Required"),
    }),
    onSubmit: () => {},
  });

  useEffect(() => {
    const currentId: any = (location.state as any)?.ticketId ?? null;
    setTicketId(currentId);
    if (currentId) {
      loadTicket(currentId);
    }
  }, [location]);

  const loadPaymentRequests = (tid: string) => {
    setPaymentRequests([]);
    getTicketPaymentRequests(tid).then(setPaymentRequests).catch(() => setPaymentRequests([]));
  };

  const loadPartsUsage = (tid: string) => {
    setPartsUsage([]);
    getTicketInventoryUsage(tid).then(setPartsUsage).catch(() => setPartsUsage([]));
  };

  const handleCancelPayment = async () => {
    if (!cancelPaymentId || !ticketId) return;
    try {
      await cancelTicketPaymentRequest(ticketId, cancelPaymentId);
      setCancelPaymentId(null);
      loadPaymentRequests(ticketId);
      setOnSuccess('Payment request cancelled');
    } catch {
      setOnError('Failed to cancel payment request');
    }
  };

  const handleReverseParts = async () => {
    if (!reversePartsId || !ticketId) return;
    try {
      await reverseTicketInventoryUsage(ticketId, reversePartsId);
      setReversePartsId(null);
      loadPartsUsage(ticketId);
      setOnSuccess('Parts usage reversed');
    } catch {
      setOnError('Failed to reverse parts usage');
    }
  };

  const loadTicket = (tid: string) => {
    getTicketById(tid)
      .then((ticket) => {
        setTicketDetails(ticket);
        setChat(ticket?.comments ?? []);
        loadPaymentRequests(tid);
        loadPartsUsage(tid);
        if (isUpdate) {
          formik.setFieldValue("category", ticket?.category ?? "");
          formik.setFieldValue("priority", ticket?.priority ?? "normal");
          formik.setFieldValue("assigneeId", ticket?.assigneeId ?? "");
          formik.setFieldValue("assigneeName", ticket?.assigneeId ?? "");
        }
      })
      .catch((err) => console.error("Error loading ticket:", err));
  };

  const sendMessage = () => {
    if (!formik.values.message.length || formik.values.img) {
      setOnError("Type a message or attach an image");
      return;
    }
    const optimistic = { authorType: "agent", body: formik.values.message, createdAt: new Date().toISOString() };
    setChat(prev => [...prev, optimistic]);
    formik.setFieldValue("message", "");

    addComment(ticketId, { body: optimistic.body })
      .then((updated) => {
        setChat(updated?.comments ?? []);
      })
      .catch(() => {
        setOnError("Error sending message");
        setChat(prev => prev.filter(c => c !== optimistic));
      });
  };

  async function uploadImage() {
    try {
      const base64Image = await takePicture();
      if (!base64Image) return;
      const file = await dataUrlToFile(base64Image);
      const res = await uploadFile(`TICKETS/`, ticketId, file);
      const body = await res.json();
      const url = body.images?.[0];
      if (!url) throw new Error('No URL returned');
      setUploadedAttachment({ url, name: file.name, mime: file.type || 'image/jpeg' });
      formik.setFieldValue("img", base64Image);
      setIsImagePreview(true);
    } catch (err) {
      setOnError("Error occurred while uploading image");
    }
  }

  const sendImage = () => {
    if (!uploadedAttachment) return;
    const optimistic = { authorType: "agent", body: "", attachments: [uploadedAttachment], createdAt: new Date().toISOString() };
    setChat(prev => [...prev, optimistic]);

    addComment(ticketId, { body: "", attachments: [uploadedAttachment] })
      .then((updated) => {
        setChat(updated?.comments ?? []);
      })
      .catch(() => {
        setOnError("Error sending image");
        setChat(prev => prev.filter(c => c !== optimistic));
      });

    formik.setFieldValue("img", null);
    setUploadedAttachment(null);
    setIsImagePreview(false);
  };

  const closeTicket = async () => {
    try {
      setLoadingState(true);
      await updateTicket(ticketId, { status: "closed" });
      setOnSuccess("Ticket closed");
      navigateTo("/tickets", { updated: true });
    } catch {
      setOnError("Error closing ticket");
    } finally {
      setLoadingState(false);
    }
  };

  const doUpdateTicket = async () => {
    if (!ticketId) { setOnError("Ticket ID not found"); return; }
    setLoadingState(true);
    try {
      const payload: any = { priority: formik.values.priority };
      if (formik.values.assigneeId) payload.assigneeId = formik.values.assigneeId;
      await updateTicket(ticketId, payload);
      setOnSuccess("Ticket updated");
      navigateTo("/tickets", { updated: true });
    } catch {
      setOnError("Error updating ticket");
    } finally {
      setLoadingState(false);
    }
  };

  const createNewTicket = async () => {
    const { category, customerUserId, customerMobile, assigneeId, plotId, priority, description } = formik.values;

    if (!category || !customerUserId || !customerMobile || !plotId) {
      setOnError("Category, customer, and plot are required.");
      return;
    }

    setLoadingState(true);
    try {
      const payload: any = {
        category,
        farmUserId: customerUserId,
        farmerMobile: customerMobile,
        plotId,
        priority,
        description,
        source: "app",
        type: "device_support",
      };
      if (assigneeId) payload.assigneeId = assigneeId;

      const res = await createTicket(payload);
      if (res?.ticketId || res?.id) {
        setOnSuccess("Ticket created");
        navigateTo("/tickets", { updated: true });
      } else {
        setOnError("Failed to create ticket");
      }
    } catch {
      setOnError("Error creating ticket");
    } finally {
      setLoadingState(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton />
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Ticket</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {/* Assigned user info */}
        {!newTicket && ticketDetails?.assigneeId && (
          <IonItem lines="none" style={{ margin: '16px', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <IonLabel>
              <h2 style={{ fontWeight: 'bold', marginBottom: '8px' }}>Assigned To:</h2>
              <p style={{ margin: '4px 0', fontSize: '14px' }}>{ticketDetails.assigneeId}</p>
            </IonLabel>
          </IonItem>
        )}

        {/* Payment requests section — only for existing tickets */}
        {!newTicket && !isUpdate && ticketId && (
          <>
            <IonItem lines="none" style={{ marginTop: 16 }}>
              <IonLabel><strong>Payment Requests</strong></IonLabel>
              <IonButton slot="end" size="small" onClick={() => setShowPaymentModal(true)}>
                + Request Payment
              </IonButton>
            </IonItem>
            {paymentRequests.length === 0 ? (
              <IonItem lines="none">
                <IonLabel color="medium" style={{ fontSize: 14 }}>No payment requests</IonLabel>
              </IonItem>
            ) : (
              paymentRequests.map((p: any) => (
                <IonCard key={p.id} style={{ margin: '8px 16px' }}>
                  <IonItem lines="none">
                    <IonLabel>
                      <h2 style={{ fontWeight: 'bold' }}>₹{p.amount}</h2>
                      <p>{p.description}</p>
                    </IonLabel>
                    <IonChip
                      color={p.status === 'PAID' ? 'success' : p.status === 'LINK_CREATED' ? 'warning' : 'medium'}
                      slot="end"
                    >
                      {p.status}
                    </IonChip>
                  </IonItem>
                  {p.paymentLink && p.status === 'LINK_CREATED' && (
                    <IonItem lines="none">
                      <IonLabel style={{ fontSize: 12 }}>
                        <a href={p.paymentLink} target="_blank" rel="noreferrer">Payment link</a>
                      </IonLabel>
                      <IonButton
                        slot="end"
                        size="small"
                        color="danger"
                        fill="outline"
                        onClick={() => setCancelPaymentId(p.id)}
                      >
                        Cancel
                      </IonButton>
                    </IonItem>
                  )}
                </IonCard>
              ))
            )}
          </>
        )}

        {/* Parts used section — only for existing open tickets */}
        {!newTicket && !isUpdate && ticketId && (
          <>
            <IonItem lines="none" style={{ marginTop: 16 }}>
              <IonLabel><strong>Parts Used</strong></IonLabel>
              <IonButton
                slot="end"
                size="small"
                onClick={() => setShowPartsModal(true)}
                disabled={ticketDetails?.status === 'closed'}
              >
                + Add Parts Used
              </IonButton>
            </IonItem>
            {partsUsage.length === 0 ? (
              <IonItem lines="none">
                <IonLabel color="medium" style={{ fontSize: 14 }}>No parts recorded</IonLabel>
              </IonItem>
            ) : (
              partsUsage.map((p: any) => {
                const isReversed = !!p.reversedByMovementId;
                const isReversal = !!p.reversalOfMovementId;
                return (
                  <IonCard
                    key={p.id}
                    style={{
                      margin: '8px 16px',
                      opacity: isReversed ? 0.6 : 1,
                      backgroundColor: isReversal ? 'var(--ion-color-warning-tint)' : undefined,
                    }}
                  >
                    <IonItem lines="none">
                      <IonLabel>
                        <h2 style={{ fontWeight: 'bold' }}>{p.quantity} × {p.itemName ?? p.inventoryItemId}</h2>
                        <p>from {p.locationName ?? p.fromInventoryLocationId}</p>
                        {p.comments && <p style={{ fontSize: 12 }}>{p.comments}</p>}
                      </IonLabel>
                      {(isReversed || isReversal) ? (
                        <IonChip
                          slot="end"
                          color={isReversed ? 'medium' : 'warning'}
                        >
                          {isReversed ? 'Reversed' : 'Reversal'}
                        </IonChip>
                      ) : isAdmin && (
                        <IonButton
                          slot="end"
                          size="small"
                          color="warning"
                          fill="outline"
                          onClick={() => setReversePartsId(p.id)}
                        >
                          Reverse
                        </IonButton>
                      )}
                    </IonItem>
                  </IonCard>
                );
              })
            )}
          </>
        )}

        {(newTicket || isUpdate) && (
          <NewTicket formik={formik} isUpdate={isUpdate} />
        )}
        <CreateTicket isUpdate={isUpdate} chat={chat} />
      </IonContent>

      <CreateTicketPaymentRequest
        ticketId={ticketId}
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onCreated={() => loadPaymentRequests(ticketId)}
      />

      <RecordPartsUsed
        ticketId={ticketId}
        isOpen={showPartsModal}
        onClose={() => setShowPartsModal(false)}
        onCreated={() => loadPartsUsage(ticketId)}
      />

      <IonAlert
        isOpen={!!cancelPaymentId}
        onDidDismiss={() => setCancelPaymentId(null)}
        header="Cancel Payment Request"
        message="Are you sure you want to cancel this payment request?"
        buttons={[
          { text: 'No', role: 'cancel' },
          { text: 'Yes', handler: handleCancelPayment },
        ]}
      />

      <IonAlert
        isOpen={!!reversePartsId}
        onDidDismiss={() => setReversePartsId(null)}
        header="Reverse Parts Usage"
        message="Reverse this parts usage? Stock will be credited back to inventory."
        buttons={[
          { text: 'No', role: 'cancel' },
          { text: 'Yes', handler: handleReverseParts },
        ]}
      />
      <IonFooter>
        {!newTicket && !isUpdate && (
          <IonItem lines="none" style={{ display: "flex", alignItems: "center" }}>
            <IonTextarea
              value={formik.values.message}
              onIonChange={(e) => formik.setFieldValue("message", e.detail.value!)}
              style={{ padding: "10px", border: "1px solid #ccc", borderRadius: "10px", resize: "vertical", fontSize: "16px", boxSizing: "border-box" }}
              placeholder="Type your message here..."
            />
            <IonItem lines="none" disabled={!!formik.values.message.length}>
              <IonIcon icon={images} slot="end" onClick={() => uploadImage()} />
            </IonItem>
            <IonButton onClick={sendMessage} fill="clear" style={{ marginLeft: "8px" }}>
              <IonIcon icon={send} slot="icon-only" />
            </IonButton>
          </IonItem>
        )}
        <IonButton
          expand="block"
          onClick={() => setShowConfirmation(true)}
          disabled={loadingState}
        >
          {loadingState
            ? isUpdate ? "Updating..." : !newTicket ? "Closing..." : "Creating..."
            : isUpdate ? "Update Ticket" : !newTicket ? "Close Ticket" : "Create Ticket"}
        </IonButton>
      </IonFooter>

      <IonToast isOpen={!!onSuccess} onDidDismiss={() => setOnSuccess("")} message={onSuccess} duration={2000} color="success" />
      <IonToast isOpen={!!onError} onDidDismiss={() => setOnError("")} message={onError} duration={2000} color="danger" />

      <IonAlert
        isOpen={showConfirmation}
        onDidDismiss={() => setShowConfirmation(false)}
        header="Confirm!"
        message={isUpdate ? "Do you want to update this ticket?" : !newTicket ? "Do you want to close this ticket?" : "Do you want to create a new ticket?"}
        buttons={[
          { text: "No", role: "cancel", cssClass: "secondary" },
          { text: "Yes", handler: () => { isUpdate ? doUpdateTicket() : !newTicket ? closeTicket() : createNewTicket(); } },
        ]}
      />

      <IonModal isOpen={imagePreview} cssClass="my-custom-class">
        <IonPage>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsImagePreview(false)}>Close</IonButton>
              </IonButtons>
              <IonTitle>Image Viewer</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent fullscreen>
            <IonItem lines="none">
              <IonCard><img src={formik.values.img} alt="attachment" /></IonCard>
            </IonItem>
            <IonButton expand="block" color="primary" onClick={sendImage}>Send</IonButton>
            <IonButton expand="block" color="danger" onClick={() => { formik.setFieldValue("img", null); setIsImagePreview(false); }}>Delete</IonButton>
          </IonContent>
        </IonPage>
      </IonModal>
    </IonPage>
  );
};

export default Ticket;
